"use client";

import { useEffect, useRef } from "react";
import { useSignUp, useUser } from "@clerk/nextjs";
import { getCookie } from "@/lib/client/cookies";
import { sendGtagEvent } from "@/lib/client/gtag";
import { sendRedditPixelEvent } from "@/lib/client/redditPixel";

const PENDING_SIGNUP_USER_KEY = "kurb_referral_signup_user";
// Separate from PENDING_SIGNUP_USER_KEY: that key drives the referral-code
// POST (only meaningful when a referral cookie exists) and gets cleared
// once that POST succeeds. The signup analytics event needs its own
// once-per-signup dedup that doesn't depend on a referral code being
// present at all, and shouldn't get cleared/re-fired by the referral flow.
const SIGNUP_EVENT_SENT_KEY = "kurb_signup_event_sent";

const StoreReferralCode = () => {
  const { user } = useUser();
  const { signUp } = useSignUp();
  const storingReferral = useRef(false);

  useEffect(() => {
    const handleReferralCode = async () => {
      const completedSignupUserId =
        signUp?.status === "complete"
          ? signUp.createdUserId
          : sessionStorage.getItem(PENDING_SIGNUP_USER_KEY);

      if (signUp?.status === "complete" && signUp.createdUserId) {
        sessionStorage.setItem(PENDING_SIGNUP_USER_KEY, signUp.createdUserId);
      }

      if (!user || completedSignupUserId !== user.id || storingReferral.current) {
        return;
      }

      // Fires regardless of whether a referral code is present — kept as
      // its own dedup key rather than piggybacking on
      // PENDING_SIGNUP_USER_KEY, since that one gets cleared once the
      // referral POST resolves (or is absent entirely for non-referred
      // signups) and would otherwise let this fire more than once.
      if (sessionStorage.getItem(SIGNUP_EVENT_SENT_KEY) !== user.id) {
        sessionStorage.setItem(SIGNUP_EVENT_SENT_KEY, user.id);
        sendGtagEvent("sign_up", "acquisition", "signup_complete");
        sendRedditPixelEvent("SignUp");
      }

      const referralCode = getCookie("referral_code");
      if (!referralCode) {
        sessionStorage.removeItem(PENDING_SIGNUP_USER_KEY);
        return;
      }

      storingReferral.current = true;
      try {
        const response = await fetch("/api/user/setReferralCode", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            referralCode,
          }),
        });

        if (!response.ok) {
          throw new Error(`Referral code request failed with status ${response.status}`);
        }

        sessionStorage.removeItem(PENDING_SIGNUP_USER_KEY);
      } catch (error) {
        console.error("Failed to store referral code:", error);
      } finally {
        storingReferral.current = false;
      }
    };
    handleReferralCode();
  }, [signUp?.createdUserId, signUp?.status, user]);

  return null;
};

export default StoreReferralCode;
